import { defineStore } from 'pinia'

interface MetaState {
  title: string
  description: string
  gtmId: string
  googleFont: string
}

export const DEFAULT_APP_FONT = 'geist-sans'
const GEIST_FONT_STACK = `'geist-sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`

const DEFAULT_TITLE = 'Check Auditor - Reduce exposición legal y fortalece tu compliance con inteligencia artificial aplicada al due diligence.'
const DEFAULT_DESCRIPTION
  = 'Reduce exposición legal y fortalece tu compliance con inteligencia artificial aplicada al due diligence.'

export const useMetaStore = defineStore('meta', {
  state: (): MetaState => ({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    gtmId: '',
    googleFont: '',
  }),

  actions: {
    setTitle(title: string) {
      const newTitle = title.trim() === '' ? DEFAULT_TITLE : title
      this.title = newTitle
      document.title = newTitle
    },

    setDescription(description: string) {
      this.description = description
      this.updateMetaTag('description', description)
    },

    updateMetaTag(name: string, content: string) {
      let meta = document.querySelector(`meta[name="${name}"]`)
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute('name', name)
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', content)
    },

    setGoogleTagManager(gtmId: string) {
      this.gtmId = gtmId

      // Inyectar el script de GTM
      if (gtmId) {
        const script = document.createElement('script')
        script.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `
        document.head.appendChild(script)
      }
    },

    setGoogleFont(fontFamily: string) {
      if (!fontFamily)
        return

      this.googleFont = fontFamily

      this.applyGoogleFont(fontFamily)
    },

    initializeGoogleFont() {
      this.applyGoogleFont(this.googleFont || DEFAULT_APP_FONT)
    },

    applyGoogleFont(fontFamily: string) {
      const selectedFont = fontFamily || DEFAULT_APP_FONT
      const root = document.documentElement

      const applyFontStack = (fontStack: string) => {
        // --font-geist-sans mantiene compatibilidad con el CSS ya servido por
        // sesiones abiertas; --app-font-family es la fuente global definitiva.
        root.style.setProperty('--font-geist-sans', fontStack)
        root.style.setProperty('--app-font-family', fontStack)
      }

      // El body usaba antes un estilo inline. Lo retiramos para que toda la
      // aplicación, incluidas las clases `font-sans`, lea la misma variable.
      document.body.style.removeProperty('font-family')

      document
        .querySelectorAll('link[data-google-font]')
        .forEach(el => el.remove())

      if (selectedFont === DEFAULT_APP_FONT) {
        applyFontStack(GEIST_FONT_STACK)
        return
      }

      const fontName = selectedFont.replace(/\+/g, ' ')
      applyFontStack(
        `"${fontName}", -apple-system, BlinkMacSystemFont, system-ui, sans-serif`,
      )

      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = `https://fonts.googleapis.com/css2?family=${selectedFont}:wght@400;500;600;700&display=swap`
      link.setAttribute('data-google-font', 'true')
      document.head.appendChild(link)
    },
  },

  persist: true,
})
